import { useState } from "react";
import { Box, Typography, Button, Alert, CircularProgress } from "@mui/material";
import { linkWithPopup, OAuthProvider, GoogleAuthProvider, FacebookAuthProvider } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import { useAuthStore } from "../store/authSlice";

export default function LinkProviderPage() {
  const navigate = useNavigate();
  const { linkProviderEmail, linkProviderCredential, clearLinkProviderChallenge } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!linkProviderEmail || !linkProviderCredential) {
    navigate("/login");
    return null;
  }

  const providerName = linkProviderCredential.providerId.replace(".com", "");

  const getProviderInstance = () => {
    switch (linkProviderCredential.providerId) {
      case "google.com":
        return new GoogleAuthProvider();
      case "facebook.com":
        return new FacebookAuthProvider();
      default:
        return new OAuthProvider(linkProviderCredential.providerId);
    }
  };

  const handleLink = async () => {
    if (!auth.currentUser) return;
    setError(null);
    setLoading(true);
    try {
      await linkWithPopup(auth.currentUser, getProviderInstance());
      clearLinkProviderChallenge();
      navigate("/");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Linking failed. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    clearLinkProviderChallenge();
    navigate("/login");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "background.default",
      }}
    >
      <Box sx={{ maxWidth: 440, width: "100%", p: 4 }}>
        <Typography variant="h3" sx={{ mb: 2 }}>
          Link your account
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          An account already exists for <strong>{linkProviderEmail}</strong>. Sign in with{" "}
          <strong>{providerName}</strong> to link it.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Button
          variant="contained"
          fullWidth
          onClick={handleLink}
          disabled={loading}
          sx={{ mb: 1.5 }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : `Sign in with ${providerName}`}
        </Button>

        <Button variant="text" fullWidth onClick={handleCancel}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
}
